import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Info } from 'lucide-react';
import {
  useCalculateDrawdown,
  useCreateDrawdownWithAllocations,
} from '@/hooks/react-query/useDrawdownCalculation';
import type {
  DrawdownCalculateRequest,
  DrawdownCalculationResult,
  PreCalculatedInvestorAllocation,
} from '@/services/drawdowns/drawdownCalculationService';
import type { Fund } from '@/types/fund';
import { formatDateForInput } from '@/utils/formatters';
import { useToast } from '@/hooks/useToast';
import { AllocationsStep } from '@/components/drawdowns/AllocationsStep';
import { DrawdownCreateHeader } from './DrawdownCreateHeader';
import { DrawdownCreateStepIndicator } from './DrawdownCreateStepIndicator';
import { DrawdownCreateParametersStep } from './DrawdownCreateParametersStep';
import { DrawdownCreateReviewStep } from './DrawdownCreateReviewStep';
import { DrawdownCreateFooterActions } from './DrawdownCreateFooterActions';
import type {
  WizardState,
  WizardStep,
  DrawdownParameters,
} from './drawdownCreateTypes';
import { WIZARD_STEPS } from './drawdownCreateTypes';

interface ParsedValidationError {
  field: string;
  message: string;
}

function parseValidationError(error: string): ParsedValidationError {
  const pydanticMatch = error.match(
    /(?:For\s+\w+\s+)?(\w+)\s+(.+?)(?:\s*\[type=|$)/
  );
  if (pydanticMatch) {
    const [, field, message] = pydanticMatch;
    return {
      field: field
        .replaceAll('_', ' ')
        .replace(/\b\w/g, (l) => l.toUpperCase()),
      message: message.replace(/For further information visit.*$/i, '').trim(),
    };
  }
  const customFieldMatch = error.match(/^(\w+(?:\s+\w+)?)[:\s]+(.+)/);
  if (customFieldMatch) {
    const [, field, message] = customFieldMatch;
    return { field: field.trim(), message: message.trim() };
  }
  return {
    field: 'Validation',
    message: error.replace(/For further information visit.*$/i, '').trim(),
  };
}

interface DrawdownCreateViewProps {
  fundId: string;
  fund: Fund;
}

export function DrawdownCreateView({ fundId, fund }: DrawdownCreateViewProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const calculateMutation = useCalculateDrawdown();
  const createMutation = useCreateDrawdownWithAllocations();

  const fundUrl = `/app/funds/${fundId}?tab=overview`;
  const drawdownsUrl = `/app/funds/${fundId}?tab=transactions&section=drawdowns`;

  const [state, setState] = useState<WizardState>(() => ({
    currentCalculation: null,
    calculationHash: null,
    parameters: {
      fundId,
      callType: 'percentage',
      inputValue: 0,
      currency: fund.base_currency || '',
      issueDate: formatDateForInput(new Date()),
      dueDate: formatDateForInput(
        new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
      ),
      purpose: '',
      description: '',
    },
    manualOverrides: new Map(),
    currentStep: 'parameters',
    hasAttemptedNext: false,
    fieldErrors: {},
    errors: [],
    warnings: [],
  }));

  const updateParameters = useCallback(
    (updates: Partial<DrawdownParameters>) => {
      setState((prev) => ({
        ...prev,
        parameters: { ...prev.parameters, ...updates },
        currentCalculation: null,
        calculationHash: null,
      }));
    },
    []
  );

  const calculateAllocations = useCallback(async () => {
    if (!state.parameters.fundId || state.parameters.inputValue <= 0) {
      setState((prev) => ({
        ...prev,
        currentCalculation: null,
        calculationHash: null,
        errors: ['Please set a valid fund and amount'],
      }));
      return;
    }

    try {
      const request: DrawdownCalculateRequest = {
        call_type: state.parameters.callType,
        input_value: state.parameters.inputValue,
        currency: state.parameters.currency,
        manual_overrides: Array.from(state.manualOverrides.values()),
      };

      const result = await calculateMutation.mutateAsync({ fundId, request });

      setState((current) => ({
        ...current,
        currentCalculation: result,
        calculationHash: result.calculation_hash,
        errors: result.errors,
        warnings: result.warnings,
      }));
    } catch (error) {
      setState((current) => ({
        ...current,
        errors: [error instanceof Error ? error.message : 'Calculation failed'],
      }));
    }
  }, [state.parameters, state.manualOverrides, calculateMutation, fundId]);

  const verifyCalculationIntegrity = useCallback(
    (calculation: DrawdownCalculationResult) =>
      calculation.calculation_hash === state.calculationHash,
    [state.calculationHash]
  );

  const validateParametersStep = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    const { parameters } = state;

    if (parameters.inputValue <= 0)
      errors.inputValue = 'Must be greater than 0';
    if (!parameters.purpose.trim()) errors.purpose = 'Title is required';
    if (!parameters.issueDate) {
      errors.issueDate = 'Issue date is required';
    } else if (parameters.issueDate > formatDateForInput(new Date())) {
      errors.issueDate = 'Issue date cannot be in the future';
    }
    if (!parameters.dueDate) {
      errors.dueDate = 'Due date is required';
    }
    if (
      parameters.issueDate &&
      parameters.dueDate &&
      parameters.dueDate <= parameters.issueDate
    ) {
      errors.dueDate = 'Due date must be after issue date';
    }

    return errors;
  };

  const canAccessStep = (step: WizardStep): boolean => {
    switch (step) {
      case 'parameters':
        return true;
      case 'allocations':
        return (
          state.parameters.inputValue > 0 &&
          state.parameters.purpose.trim() !== '' &&
          state.parameters.issueDate !== '' &&
          state.parameters.dueDate !== ''
        );
      case 'review':
        return (
          !!state.currentCalculation &&
          verifyCalculationIntegrity(state.currentCalculation)
        );
      default:
        return false;
    }
  };

  const handleNext = () => {
    const currentIndex = WIZARD_STEPS.indexOf(state.currentStep);

    if (state.currentStep === 'parameters') {
      const errors = validateParametersStep();
      if (Object.keys(errors).length > 0) {
        setState((prev) => ({
          ...prev,
          hasAttemptedNext: true,
          fieldErrors: errors,
        }));
        return;
      }
    }

    setState((prev) => ({
      ...prev,
      currentStep: WIZARD_STEPS[currentIndex + 1],
      hasAttemptedNext: false,
      fieldErrors: {},
      errors: [],
      warnings: [],
    }));
  };

  const handlePrevious = () => {
    const currentIndex = WIZARD_STEPS.indexOf(state.currentStep);
    setState((prev) => ({
      ...prev,
      currentStep: WIZARD_STEPS[currentIndex - 1],
      errors: [],
      warnings: [],
    }));
  };

  const handleSubmit = async () => {
    if (
      !state.currentCalculation ||
      !verifyCalculationIntegrity(state.currentCalculation)
    ) {
      setState((prev) => ({
        ...prev,
        errors: ['Please recalculate allocations before submitting'],
      }));
      return;
    }

    try {
      const allocations: PreCalculatedInvestorAllocation[] =
        state.currentCalculation.allocations.map((alloc) => ({
          ...alloc,
          override_reason: alloc.is_manual_override
            ? 'Manual adjustment'
            : undefined,
        }));

      const result = await createMutation.mutateAsync({
        fundId,
        request: {
          call_type: state.parameters.callType,
          input_value: state.parameters.inputValue,
          currency: state.parameters.currency,
          issue_date: state.parameters.issueDate,
          due_date: state.parameters.dueDate,
          purpose: state.parameters.purpose,
          description: state.parameters.description,
          allocations,
          calculation_hash: state.calculationHash!,
        },
      });

      navigate(
        `/app/funds/${fundId}/drawdowns/${result.capital_call_id}/preview`
      );
    } catch (error) {
      setState((prev) => ({
        ...prev,
        errors: [
          error instanceof Error ? error.message : 'Failed to create drawdown',
        ],
      }));
    }
  };

  const isLoading = calculateMutation.isPending || createMutation.isPending;
  const canSubmit =
    !!state.currentCalculation &&
    verifyCalculationIntegrity(state.currentCalculation);

  return (
    <div className="p-6 space-y-6">
      <DrawdownCreateHeader
        fundName={fund.display_name || fund.name}
        backTo={drawdownsUrl}
        breadcrumbs={[
          { label: 'Fund', href: fundUrl },
          { label: 'Drawdowns', href: drawdownsUrl },
          { label: 'New Capital Call' },
        ]}
      />

      <div className="bg-card border rounded-lg p-6 space-y-6">
        <DrawdownCreateStepIndicator
          currentStep={state.currentStep}
          canAccessStep={canAccessStep}
          onStepClick={(step) =>
            setState((prev) => ({ ...prev, currentStep: step }))
          }
        />

        {state.errors.length > 0 && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>
              {state.errors.length === 1
                ? 'Validation Error'
                : `${state.errors.length} Validation Errors`}
            </AlertTitle>
            <AlertDescription>
              <div className="space-y-1">
                {state.errors.map((error) => {
                  const parsed = parseValidationError(error);
                  return (
                    <div key={error} className="text-sm">
                      <span className="font-medium">{parsed.field}:</span>{' '}
                      {parsed.message}
                    </div>
                  );
                })}
              </div>
            </AlertDescription>
          </Alert>
        )}

        {state.warnings.length > 0 && (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertTitle>
              {state.warnings.length === 1
                ? 'Warning'
                : `${state.warnings.length} Warnings`}
            </AlertTitle>
            <AlertDescription>
              <ul className="list-disc list-inside space-y-1">
                {state.warnings.map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        <div className="min-h-[400px]">
          {state.currentStep === 'parameters' && (
            <DrawdownCreateParametersStep
              parameters={state.parameters}
              fund={fund}
              hasAttemptedNext={state.hasAttemptedNext}
              fieldErrors={state.fieldErrors}
              onUpdate={updateParameters}
            />
          )}

          {state.currentStep === 'allocations' && (
            <div>
              <div className="mb-4">
                <h3 className="text-lg font-semibold">Investor Allocations</h3>
                <p className="text-sm text-muted-foreground">
                  Review and adjust the calculated allocations for each investor
                </p>
              </div>
              <AllocationsStep
                calculation={state.currentCalculation}
                currency={state.parameters.currency}
                isLoading={isLoading}
                onCalculate={calculateAllocations}
                onEditAllocation={() =>
                  toast({
                    title: 'Feature coming soon',
                    description:
                      'Manual overrides will be available in a future update',
                  })
                }
              />
            </div>
          )}

          {state.currentStep === 'review' && state.currentCalculation && (
            <DrawdownCreateReviewStep
              parameters={state.parameters}
              calculation={state.currentCalculation}
              fund={fund}
            />
          )}
        </div>

        <DrawdownCreateFooterActions
          currentStep={state.currentStep}
          isLoading={isLoading}
          canSubmit={canSubmit}
          onPrevious={handlePrevious}
          onNext={handleNext}
          onCancel={() => navigate(drawdownsUrl)}
          onSubmit={handleSubmit}
          onRecalculate={calculateAllocations}
        />
      </div>
    </div>
  );
}
